import React, { useEffect, useState, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faSave, faInfoCircle } from '@fortawesome/free-solid-svg-icons';
import userService from '../../services/userService';
import deliveryOrderService from '../../services/deliveryOrderService';
import { UI_COLORS, colorHelpers } from '../../utils/colors';
import validateUtils from '../../utils/validate';
import { useTranslation } from 'react-i18next';

import SearchableSelect from '../common/SearchableSelect';

const AssignDeliveryPersonnel = ({ isOpen, orderId, initialValues = null, onClose, onSubmit }) => {
    const { t } = useTranslation();
    const loadingRef = useRef(false);

    const [supervisors, setSupervisors] = useState([]);
    const [loading, setLoading] = useState(false);
    const [fetchingDetails, setFetchingDetails] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);

    const [selectedSupervisor, setSelectedSupervisor] = useState('');
    const [initialSupervisorName, setInitialSupervisorName] = useState(null);
    const [vehicleNumber, setVehicleNumber] = useState('');
    const [driverContact, setDriverContact] = useState('');
    const [showVehicleTooltip, setShowVehicleTooltip] = useState(false);
    const [internalRemark, setInternalRemark] = useState('');

    const formStrings = t('deliveryOrdersModule.forms.assignPersonnel', { returnObjects: true });

    // Fetch details for Edit mode or pre-fill from initialValues
    useEffect(() => {
        const loadInitialData = async () => {
            if (!isOpen) return;

            // If we have an orderId and it's an edit (has supervisor already), fetch fresh details
            if (orderId && initialValues?.delivery_supervisor_id) {
                setFetchingDetails(true);
                try {
                    const res = await deliveryOrderService.getDeliverySubOrderById(orderId);
                    if (res.success && res.data) {
                        const d = res.data;
                        setSelectedSupervisor(d.delivery_supervisor_id ? String(d.delivery_supervisor_id) : '');
                        setInitialSupervisorName(d.supervisor_name || d.delivery_supervisor_name || null);
                        setVehicleNumber(d.vehicle_number || '');
                        setDriverContact(d.driver_contact || '');
                        setInternalRemark(d.internal_remark || '');
                        return; // Exit early as we've populated from API
                    }
                } catch (e) {
                    console.error('Failed to fetch fresh order details', e);
                } finally {
                    setFetchingDetails(false);
                }
            }

            // Fallback to initialValues or reset if no orderId/fetch failed
            if (initialValues) {
                const sup = initialValues.delivery_supervisor_id || initialValues.deliverySupervisorId || null;
                const supName = initialValues.supervisor_name || initialValues.delivery_supervisor_name || initialValues.deliverySupervisorName || initialValues.supervisorName || null;
                setInitialSupervisorName(supName || null);
                setSelectedSupervisor(sup ? String(sup) : '');
                setVehicleNumber(initialValues.vehicle_number || initialValues.vehicleNumber || '');
                setDriverContact(initialValues.driver_contact || initialValues.driverContact || '');
                setInternalRemark(initialValues.internal_remark || '');
            } else {
                setSelectedSupervisor('');
                setInitialSupervisorName(null);
                setVehicleNumber('');
                setDriverContact('');
                setInternalRemark('');
            }
        };

        loadInitialData();
    }, [isOpen, orderId, initialValues]);

    useEffect(() => {
        if (!isOpen) return;

        const processUsers = (users) => {
            const normalizedUsers = (users || []).map(u => {
                const name = u.name || `${u.first_name || ''} ${u.last_name || ''}`.trim() || u.email;
                const isActive = !!u.is_active_now;
                const pendingCount = u.pending_orders_count || 0;

                return {
                    ...u,
                    name,
                    displayName: name,
                    style: {
                        backgroundColor: isActive
                            ? colorHelpers.withOpacity(UI_COLORS.STATUS.SUCCESS, 0.2)
                            : colorHelpers.withOpacity(UI_COLORS.STATUS.ERROR, 0.2)
                    },
                    richLabel: (
                        <div className="flex items-center justify-between w-full">
                            <span>{name}</span>
                            <div className="flex items-center gap-2 text-[10px] uppercase tracking-wider font-bold">
                                <span style={{ color: isActive ? UI_COLORS.STATUS.SUCCESS : UI_COLORS.STATUS.ERROR }}>
                                    ({isActive ? 'Available' : 'Not Available'})
                                </span>
                                <span style={{ color: UI_COLORS.TEXT.SECONDARY }}>
                                    - {pendingCount} Pending
                                </span>
                            </div>
                        </div>
                    )
                };
            });

            let supList = normalizedUsers;

            // Ensure pre-selected users are in the list even if they weren't returned by status API
            if (initialValues) {
                const supId = initialValues.delivery_supervisor_id || initialValues.deliverySupervisorId;
                if (supId) {
                    const preSelectedSup = normalizedUsers.find(u => String(u.id) === String(supId));
                    if (preSelectedSup) {
                        setSelectedSupervisor(String(preSelectedSup.id));
                    }
                }

                const supName = initialValues.supervisor_name || initialValues.delivery_supervisor_name || initialValues.deliverySupervisorName || initialValues.supervisorName;
                if (supName && !supId) {
                    const match = normalizedUsers.find(u => u.name && u.name.trim().toLowerCase() === supName.trim().toLowerCase());
                    if (match) {
                        setSelectedSupervisor(String(match.id));
                        setInitialSupervisorName(null);
                    }
                }
            }

            setSupervisors(supList);
        };

        const loadData = async () => {
            if (loadingRef.current) return;

            loadingRef.current = true;
            setLoading(true);
            setError(null);
            try {
                const res = await userService.getDeliverySupervisorsStatus();
                if (res.success) {
                    const all = Array.isArray(res.data) ? res.data : [];
                    processUsers(all);
                } else {
                    processUsers([]);
                }
            } catch (err) {
                console.error("Failed to load supervisors", err);
                setError(formStrings.messages.loadingUsers);
            } finally {
                setLoading(false);
                loadingRef.current = false;
            }
        };

        loadData();
    }, [isOpen, initialValues, formStrings.messages.loadingUsers]);

    if (!isOpen) return null;

    const handleSubmit = async () => {
        setError(null);
        if (!orderId) return setError(formStrings.messages.invalidOrder);

        const supId = selectedSupervisor ? Number(selectedSupervisor) : null;

        if (!supId) return setError(formStrings.messages.selectAtLeastOne);

        if (!vehicleNumber || String(vehicleNumber).trim() === '') {
            setSubmitting(false);
            return setError(formStrings.messages.vehicleRequired);
        } else if (!validateUtils.isValidVehicleNumber(vehicleNumber)) {
            setSubmitting(false);
            return setError(formStrings.messages.invalidVehicleNumber);
        }

        if (!driverContact || String(driverContact).trim() === '') {
            setSubmitting(false);
            return setError(formStrings.messages.driverContactRequired);
        } else if (!validateUtils.isValidPhoneNumber(driverContact)) {
            setSubmitting(false);
            return setError(formStrings.messages.invalidDriverContact);
        }

        setSubmitting(true);
        try {
            const payload = {};
            if (supId) payload.delivery_supervisor_id = supId;
            payload.vehicle_number = String(vehicleNumber).trim();
            payload.driver_contact = String(driverContact).trim();
            payload.internal_remark = internalRemark || '';

            if (onSubmit) {
                const result = await onSubmit(orderId, payload);
                if (result && result.success) {
                    onClose && onClose();
                } else {
                    setError(result?.error || formStrings.messages.assignFailed);
                }
            } else {
                setError("No submit handler provided");
            }
        } catch (err) {
            setError(err?.message || formStrings.messages.assignFailed);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ backgroundColor: UI_COLORS.BACKGROUNDS.OVERLAY }}>
            <div className="w-full max-w-2xl bg-white rounded-2xl shadow-2xl relative" style={{ backgroundColor: UI_COLORS.BACKGROUNDS.CARD }}>

                {/* Header */}
                <div className="flex items-center justify-between p-6 border-b" style={{ borderColor: UI_COLORS.BORDERS.LIGHT }}>
                    <div>
                        <h2 className="text-2xl font-bold" style={{ color: UI_COLORS.TEXT.PRIMARY }}>
                            {initialValues?.delivery_supervisor_id ? formStrings.editTitle : formStrings.title}
                        </h2>
                        <p className="mt-1 text-sm" style={{ color: UI_COLORS.TEXT.SECONDARY }}>{formStrings.subtitle}</p>
                    </div>
                    <button
                        onClick={onClose}
                        className="p-2 rounded-lg transition-colors"
                        style={{ color: UI_COLORS.TEXT.MUTED, backgroundColor: colorHelpers.withOpacity(UI_COLORS.TEXT.MUTED, 0.08) }}
                    >
                        <FontAwesomeIcon icon={faTimes} />
                    </button>
                </div>

                {/* Body */}
                <div className="p-6">
                    {loading ? (
                        <div className="flex flex-col items-center justify-center py-10">
                            <div className="h-10 w-10 animate-spin rounded-full border-4 border-b-transparent" style={{ borderColor: colorHelpers.withOpacity(UI_COLORS.BRAND.PRIMARY, 0.5) }}></div>
                            <p className="mt-4 text-sm font-semibold" style={{ color: UI_COLORS.TEXT.SECONDARY }}>{formStrings.messages.loadingUsers}</p>
                        </div>
                    ) : (
                        <div className="space-y-6">
                            <div className="grid grid-cols-1 gap-6">
                                <div>
                                    <label className="block text-sm font-semibold mb-2" style={{ color: UI_COLORS.FORMS.LABEL }}>{formStrings.labels.supervisor} <span>*</span></label>
                                    <SearchableSelect
                                        value={selectedSupervisor}
                                        onChange={(e) => setSelectedSupervisor(e.target.value)}
                                        disabled={fetchingDetails}
                                        options={supervisors.map(s => ({
                                            value: s.id,
                                            label: s.name,
                                            richLabel: s.richLabel,
                                            style: s.style
                                        }))}
                                        placeholder={initialSupervisorName || formStrings.labels.supervisor}
                                        className="w-full"
                                    />
                                </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div>
                                    <label className="block text-sm font-semibold mb-2" style={{ color: UI_COLORS.FORMS.LABEL }}>
                                        {formStrings.labels.vehicleNumber} <span>*</span>
                                        <span
                                            className="ml-2 cursor-pointer relative inline-block"
                                            onMouseEnter={() => setShowVehicleTooltip(true)}
                                            onMouseLeave={() => setShowVehicleTooltip(false)}
                                            style={{ color: UI_COLORS.TEXT.MUTED }}
                                        >
                                            <FontAwesomeIcon icon={faInfoCircle} />
                                            {showVehicleTooltip && (
                                                <span
                                                    className="absolute left-0 top-6 z-50 px-3 py-2 text-xs font-normal rounded-lg shadow-lg"
                                                    style={{
                                                        backgroundColor: UI_COLORS.BACKGROUNDS.CARD,
                                                        color: UI_COLORS.TEXT.PRIMARY,
                                                        border: `1px solid ${UI_COLORS.BORDERS.MEDIUM}`,
                                                        minWidth: '200px',
                                                    }}
                                                >
                                                    <div>{formStrings.labels.vehicleNumberHint}</div>
                                                </span>
                                            )}
                                        </span>
                                    </label>
                                    <input
                                        value={vehicleNumber}
                                        onChange={(e) => setVehicleNumber(e.target.value)}
                                        disabled={fetchingDetails}
                                        className="w-full px-4 py-3 rounded-xl border-2"
                                        placeholder={formStrings.placeholders?.vehicleNumber || "e.g., KA-53-1234"}
                                        style={{ backgroundColor: UI_COLORS.FORMS.INPUT.background, borderColor: UI_COLORS.FORMS.INPUT.border }}
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-semibold mb-2" style={{ color: UI_COLORS.FORMS.LABEL }}>{formStrings.labels.driverContact} <span>*</span></label>
                                    <input
                                        value={driverContact}
                                        onChange={(e) => setDriverContact(validateUtils.formatNumericInput(e.target.value, { maxLength: 10 }))}
                                        disabled={fetchingDetails}
                                        className="w-full px-4 py-3 rounded-xl border-2"
                                        placeholder={formStrings.placeholders?.driverContact || "e.g., 9876543210"}
                                        style={{ backgroundColor: UI_COLORS.FORMS.INPUT.background, borderColor: UI_COLORS.FORMS.INPUT.border }}
                                    />
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm font-semibold mb-2" style={{ color: UI_COLORS.FORMS.LABEL }}>{t('common.formFields.labels.internalRemarks')}</label>
                                <textarea
                                    value={internalRemark}
                                    onChange={(e) => setInternalRemark(e.target.value)}
                                    disabled={fetchingDetails}
                                    className="w-full px-4 py-3 rounded-xl border-2 resize-none"
                                    rows="2"
                                    placeholder={t('common.formFields.placeholders.internalRemarksOptional')}
                                    style={{ backgroundColor: '#f0fdf4', borderColor: '#86efac' }}
                                />
                            </div>

                            {error && (
                                <div className="p-4 rounded-xl text-sm font-medium" style={{ backgroundColor: colorHelpers.withOpacity(UI_COLORS.STATUS.ERROR, 0.1), color: UI_COLORS.STATUS.ERROR }}>
                                    {error}
                                </div>
                            )}
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="flex items-center justify-end gap-4 p-6 border-t" style={{ borderColor: UI_COLORS.BORDERS.LIGHT }}>
                    <button
                        type="button"
                        onClick={onClose}
                        className="px-6 py-3 rounded-xl font-medium transition-colors"
                        disabled={submitting}
                        style={{ backgroundColor: UI_COLORS.BUTTONS.SECONDARY.background, color: UI_COLORS.BUTTONS.SECONDARY.text }}
                    >
                        {formStrings.buttons.cancel}
                    </button>

                    <button
                        type="button"
                        onClick={handleSubmit}
                        className="px-6 py-3 rounded-xl font-semibold shadow-lg transition-all hover:shadow-xl disabled:opacity-70 disabled:cursor-not-allowed"
                        disabled={submitting || loading || fetchingDetails}
                        style={{ backgroundColor: UI_COLORS.BUTTONS.PRIMARY.background, color: UI_COLORS.BUTTONS.PRIMARY.text }}
                    >
                        <span className="inline-flex items-center">
                            {submitting && (
                                <span className="mr-2 animate-spin">
                                    <div className="h-4 w-4 rounded-full border-2 border-b-transparent" style={{ borderColor: colorHelpers.withOpacity(UI_COLORS.BUTTONS.PRIMARY.text, 0.6) }}></div>
                                </span>
                            )}
                            {!submitting && <FontAwesomeIcon icon={faSave} className="mr-2" />}
                            {submitting ? (initialValues?.delivery_supervisor_id ? formStrings.buttons.changing : formStrings.buttons.assigning) : (initialValues?.delivery_supervisor_id ? formStrings.buttons.change : formStrings.buttons.assign)}
                        </span>
                    </button>
                </div>
            </div>
        </div>
    );
};

export default AssignDeliveryPersonnel;
